import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Machine } from '../../../types/machine';
import type { Roaster } from '../../../types/roaster';
import { fetchWorkOrderReferenceData, type WorkOrderReferenceData } from './workOrderReferenceData';

export const WORK_ORDER_REFERENCE_QUERY_KEY = ['work-order-reference-data'] as const;

const EMPTY_REFERENCE_DATA: WorkOrderReferenceData = {
  recipes: [],
  greenBeans: [],
  artisanProfiles: [],
  machines: [],
  roasters: [],
};

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/**
 * Bulk mode renders up to 20 cards. Without a shared cache every card would
 * refetch the same six reference lists, so they all read this one query.
 * Bulk is hidden during onboarding, so sample rows are never requested here.
 */
export const useWorkOrderReferenceData = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: WORK_ORDER_REFERENCE_QUERY_KEY,
    queryFn: () => fetchWorkOrderReferenceData(false),
    staleTime: 5 * 60 * 1000,
  });

  const addMachine = useCallback(
    (machine: Machine) => {
      queryClient.setQueryData<WorkOrderReferenceData>(WORK_ORDER_REFERENCE_QUERY_KEY, (prev) =>
        prev ? { ...prev, machines: [...prev.machines, machine].sort(byName) } : prev,
      );
    },
    [queryClient],
  );

  const addRoaster = useCallback(
    (roaster: Roaster) => {
      queryClient.setQueryData<WorkOrderReferenceData>(WORK_ORDER_REFERENCE_QUERY_KEY, (prev) =>
        prev ? { ...prev, roasters: [...prev.roasters, roaster].sort(byName) } : prev,
      );
    },
    [queryClient],
  );

  return {
    ...(query.data ?? EMPTY_REFERENCE_DATA),
    isLoading: query.isPending,
    addMachine,
    addRoaster,
  };
};
